/**
 * Map a CONCATENATED-time range of a multi-part pandora item to a single part
 * plus part-local in/out. The range is clamped to the part containing
 * `concatIn` (a clip addresses one part), and the out point is floored to the
 * shortest clip the timeline format accepts.
 *
 * That floor used to be 0.5s — twelve frames at 25fps — a UI convenience
 * borrowed from ItemDetail's preset markers. Every path that places archive
 * material on the timeline goes through here, so it silently rewrote genuinely
 * short ranges: `machinedescriptions` annotations can be ~0.04s (one frame),
 * and a search result for one arrived on the timeline as half a second
 * of something nobody asked for.
 */
import { MIN_CLIP_DURATION } from './invariants';

/**
 * The shortest clip we will PLACE, as opposed to the shortest the format will
 * hold (`MIN_CLIP_DURATION`, 0.05s). Three frames at 25fps: short enough to
 * still read as the single shot that was searched for, long enough to see when
 * scrubbing and to grab on the timeline without zooming all the way in.
 *
 * Two constants because they answer different questions. `MIN_CLIP_DURATION` is
 * a format invariant — go below it and the edit is invalid. This is a judgement
 * about usability, and is expected to be the larger of the two.
 */
export const MIN_PLACED_DURATION = 0.12;

const MIN_RANGE = Math.max(MIN_PLACED_DURATION, MIN_CLIP_DURATION);

export interface PartRange {
  part: number; // 1-based
  inPoint: number; // part-local seconds
  outPoint: number;
}

export function concatRangeToPartRange(
  durations: number[],
  concatIn: number,
  concatOut: number,
): PartRange {
  if (durations.length === 0) {
    const inPoint = Math.max(0, concatIn);
    return { part: 1, inPoint, outPoint: Math.max(concatOut, inPoint + MIN_RANGE) };
  }
  let acc = 0;
  for (let p = 0; p < durations.length; p++) {
    if (concatIn < acc + durations[p] || p === durations.length - 1) {
      const inPoint = Math.min(Math.max(0, concatIn - acc), Math.max(0, durations[p] - MIN_RANGE));
      const outPoint = Math.min(Math.max(concatOut - acc, inPoint + MIN_RANGE), durations[p]);
      return { part: p + 1, inPoint, outPoint };
    }
    acc += durations[p];
  }
  // unreachable — the last part always matches above
  return { part: 1, inPoint: 0, outPoint: MIN_RANGE };
}
